import { api } from "../lib/api";

export type CategoryKind = "income" | "expense";
export type TaxGroup =
  | "turnover"
  | "cost_of_goods"
  | "staff"
  | "premises"
  | "office_admin"
  | "travel"
  | "motor"
  | "professional_fees"
  | "bank_interest_charges"
  | "loan_interest"
  | "loan_principal"
  | "loan_received"
  | "equipment_capital"
  | "marketing"
  | "insurance"
  | "subsistence"
  | "training"
  | "software_subscriptions"
  | "other"
  | "non_allowable"
  | "vat_payment"
  | "tax_payment"
  | "owner_drawings";

export interface Category {
  id: string;
  company_id: string;
  name: string;
  kind: CategoryKind;
  tax_group: TaxGroup;
  allowable_for_tax: boolean;
  sort_order: number;
  created_at: string;
  updated_at: string;
}

export interface CategoryInput {
  name: string;
  kind: CategoryKind;
  tax_group: TaxGroup;
  allowable_for_tax?: boolean;
  sort_order?: number;
}

export type MatchType = "contains" | "starts_with" | "regex";
export type RuleDirection = "in" | "out" | "any";

export interface CategoryRule {
  id: string;
  company_id: string;
  priority: number;
  match_type: MatchType;
  pattern: string;
  amount_min: string | null;
  amount_max: string | null;
  direction: RuleDirection;
  category_id: string | null;
  category_name: string | null;
  loan_id: string | null;
  /** Server-joined display field, same convention as category_name. */
  loan_name: string | null;
  set_is_transfer: boolean;
  set_is_personal: boolean;
  created_at: string;
  updated_at: string;
}

export interface CategoryRuleInput {
  priority?: number;
  match_type: MatchType;
  pattern: string;
  amount_min?: string | null;
  amount_max?: string | null;
  direction?: RuleDirection;
  category_id?: string | null;
  loan_id?: string | null;
  set_is_transfer?: boolean;
  set_is_personal?: boolean;
}

export interface RuleChange {
  transaction_id: string;
  description: string;
  date: string;
  amount: string;
  rule_id: string;
  category_id: string | null;
  category_name: string | null;
  loan_id: string | null;
  /** Server-joined display field, same convention as category_name. */
  loan_name: string | null;
  set_is_transfer: boolean;
  set_is_personal: boolean;
}

export interface ApplyRulesResult {
  dry_run: boolean;
  changes: RuleChange[];
}

export function listCategories(): Promise<Category[]> {
  return api.get<Category[]>("/api/categories");
}

export function getCategory(id: string): Promise<Category> {
  return api.get<Category>(`/api/categories/${id}`);
}

export function createCategory(data: CategoryInput): Promise<Category> {
  return api.post<Category>("/api/categories", data);
}

export function updateCategory(
  id: string,
  data: CategoryInput & { updated_at: string },
): Promise<Category> {
  return api.put<Category>(`/api/categories/${id}`, data);
}

export function deleteCategory(id: string): Promise<void> {
  return api.delete(`/api/categories/${id}`);
}

export function listCategoryRules(): Promise<CategoryRule[]> {
  return api.get<CategoryRule[]>("/api/category-rules");
}

export function getCategoryRule(id: string): Promise<CategoryRule> {
  return api.get<CategoryRule>(`/api/category-rules/${id}`);
}

export function createCategoryRule(data: CategoryRuleInput): Promise<CategoryRule> {
  return api.post<CategoryRule>("/api/category-rules", data);
}

export function updateCategoryRule(
  id: string,
  data: CategoryRuleInput & { updated_at: string },
): Promise<CategoryRule> {
  return api.put<CategoryRule>(`/api/category-rules/${id}`, data);
}

export function deleteCategoryRule(id: string): Promise<void> {
  return api.delete(`/api/category-rules/${id}`);
}

export function applyCategoryRules(dryRun: boolean): Promise<ApplyRulesResult> {
  return api.post<ApplyRulesResult>(`/api/category-rules/apply?dry_run=${String(dryRun)}`);
}
